import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTenant } from "../lib/tenant-context";
import { useAuth } from "../lib/auth-context";

// Tenant-specific branding configuration
const TENANT_BRANDING: Record<
  string,
  {
    name: string;
    initial: string;
    tagline: string;
    description: string;
    primaryColor: string;
  }
> = {
  lululemon: {
    name: "lululemon",
    initial: "L",
    tagline: "Product Intelligence Cloud",
    description:
      "AI-powered product understanding, in-store intelligence, and lifecycle management for your retail operations.",
    primaryColor: "#C41230",
  },
  arcteryx: {
    name: "Arc'teryx",
    initial: "A",
    tagline: "ReBird Intelligence Platform",
    description:
      "AI-powered trade-in management, product lifecycle tracking, and circular commerce intelligence for sustainable retail.",
    primaryColor: "#1a1a1a",
  },
  nespresso: {
    name: "Nespresso",
    initial: "N",
    tagline: "Recycling Intelligence Hub",
    description:
      "AI-powered capsule recycling, customer engagement, and sustainability tracking for circular operations.",
    primaryColor: "#6B4423",
  },
  lego: {
    name: "LEGO",
    initial: "L",
    tagline: "Brick Lifecycle Platform",
    description:
      "AI-powered set identification, brick sorting intelligence, and circular play experiences for sustainable building.",
    primaryColor: "#FFD500",
  },
  nike: {
    name: "Nike",
    initial: "N",
    tagline: "Just Do It",
    description:
      "AI-powered trade-in management, footwear authentication, and circular commerce for sustainable sneaker retail.",
    primaryColor: "#111111",
  },
  adidas: {
    name: "adidas",
    initial: "A",
    tagline: "Impossible Is Nothing",
    description:
      "AI-powered trade-in management, product authentication, and circular commerce for sustainable sportswear retail.",
    primaryColor: "#000000",
  },
  demobrand: {
    name: "Demo Brand",
    initial: "D",
    tagline: "Product Intelligence Cloud",
    description:
      "AI-powered product understanding, trade-in management, and lifecycle intelligence for modern retail operations.",
    primaryColor: "#0066CC",
  },
};

interface Program {
  id: string;
  name: string;
  type: string;
  status: string;
}

interface DashboardStats {
  // Knowledge
  knowledgeItems: number;
  knowledgeIndexed: number;
  knowledgeHealth: number;
  countsByType: {
    product: number;
    material: number;
    policy: number;
    faq: number;
    training: number;
    custom: number;
  };

  // Products
  products: number;

  // Programs
  activePrograms: number;
  totalPrograms: number;

  // PIN/AI
  aiConversations: number;
  aiMinutesUsed: number;
  pinTakebacks: number;

  // Treasury
  treasuryBalance: number;
  creditIssued: number;

  // Stores
  activeStores: number;
}

interface RecentActivity {
  id: string;
  type: "conversation" | "takeback" | "knowledge";
  title: string;
  subtitle: string;
  time: string;
}

// Default stats for fallback
const DEFAULT_STATS: DashboardStats = {
  knowledgeItems: 0,
  knowledgeIndexed: 0,
  knowledgeHealth: 0,
  countsByType: {
    product: 0,
    material: 0,
    policy: 0,
    faq: 0,
    training: 0,
    custom: 0,
  },
  products: 0,
  activePrograms: 0,
  totalPrograms: 0,
  aiConversations: 0,
  aiMinutesUsed: 0,
  pinTakebacks: 0,
  treasuryBalance: 0,
  creditIssued: 0,
  activeStores: 0,
};

// Tenant-specific demo data (used as fallback when APIs fail)
function getTenantDemoData(tenantId: string): {
  stats: DashboardStats;
  activities: RecentActivity[];
  programs: Program[];
} {
  if (tenantId === "arcteryx") {
    return {
      stats: {
        ...DEFAULT_STATS,
        products: 10,
        knowledgeItems: 31,
        knowledgeIndexed: 29,
        knowledgeHealth: 94,
        countsByType: {
          product: 10,
          material: 5,
          policy: 5,
          faq: 8,
          training: 3,
          custom: 0,
        },
        aiConversations: 186,
        pinTakebacks: 47,
        activePrograms: 2,
        totalPrograms: 3,
        treasuryBalance: 18650,
        creditIssued: 4200,
        activeStores: 5,
      },
      activities: [
        {
          id: "arc-1",
          type: "takeback",
          title: "Trade-in completed",
          subtitle: "Alex Thompson - Beta AR Jacket + Atom LT ($400 credit)",
          time: "36 min ago",
        },
        {
          id: "arc-2",
          type: "conversation",
          title: "AI Educator conversation",
          subtitle: "Guest asked about GORE-TEX Pro vs regular GORE-TEX",
          time: "1 hour ago",
        },
        {
          id: "arc-3",
          type: "takeback",
          title: "Trade-in completed",
          subtitle: "Jordan McKay - Cerium LT + Gamma LT ($315 credit)",
          time: "2 hours ago",
        },
        {
          id: "arc-4",
          type: "knowledge",
          title: "Knowledge indexed",
          subtitle: "Arc'teryx materials guide synced",
          time: "3 hours ago",
        },
      ],
      programs: [
        {
          id: "prg_rebird",
          name: "ReBird Trade-In",
          type: "takeback",
          status: "active",
        },
        {
          id: "prg_resale",
          name: "ReBird Resale",
          type: "resale",
          status: "active",
        },
        {
          id: "prg_repair",
          name: "Repair Service",
          type: "service",
          status: "draft",
        },
      ],
    };
  } else if (tenantId === "nespresso") {
    return {
      stats: {
        ...DEFAULT_STATS,
        products: 4,
        knowledgeItems: 28,
        knowledgeIndexed: 26,
        knowledgeHealth: 93,
        countsByType: {
          product: 4,
          material: 5,
          policy: 6,
          faq: 10,
          training: 2,
          custom: 1,
        },
        aiConversations: 312,
        pinTakebacks: 1847,
        activePrograms: 1,
        totalPrograms: 2,
        treasuryBalance: 4620,
        creditIssued: 920,
        activeStores: 12,
      },
      activities: [
        {
          id: "nes-1",
          type: "takeback",
          title: "Capsule recycling completed",
          subtitle: "Sarah M. - 45 capsules recycled (+90 points)",
          time: "15 min ago",
        },
        {
          id: "nes-2",
          type: "conversation",
          title: "AI Assistant conversation",
          subtitle: "Guest asked about recycling drop-off locations",
          time: "45 min ago",
        },
        {
          id: "nes-3",
          type: "takeback",
          title: "Capsule recycling completed",
          subtitle: "Michael R. - 120 capsules recycled (+240 points)",
          time: "1 hour ago",
        },
        {
          id: "nes-4",
          type: "knowledge",
          title: "Sustainability report updated",
          subtitle: "Q4 recycling metrics synced",
          time: "2 hours ago",
        },
      ],
      programs: [
        {
          id: "prg_recycle",
          name: "Capsule Recycling",
          type: "takeback",
          status: "active",
        },
        {
          id: "prg_rewards",
          name: "Recycling Rewards",
          type: "rewards",
          status: "draft",
        },
      ],
    };
  } else if (tenantId === "lego") {
    return {
      stats: {
        ...DEFAULT_STATS,
        products: 10,
        knowledgeItems: 31,
        knowledgeIndexed: 29,
        knowledgeHealth: 94,
        countsByType: {
          product: 10,
          material: 3,
          policy: 6,
          faq: 8,
          training: 3,
          custom: 1,
        },
        aiConversations: 423,
        pinTakebacks: 2341,
        activePrograms: 2,
        totalPrograms: 3,
        treasuryBalance: 34200,
        creditIssued: 8450,
        activeStores: 8,
      },
      activities: [
        {
          id: "lego-1",
          type: "takeback",
          title: "Brick Box received",
          subtitle: "Thomas K. - 2.3kg mixed bricks ($28 credit)",
          time: "12 min ago",
        },
        {
          id: "lego-2",
          type: "conversation",
          title: "AI Assistant conversation",
          subtitle: "Guest asked about Technic Porsche 911 GT3 RS availability",
          time: "35 min ago",
        },
        {
          id: "lego-3",
          type: "takeback",
          title: "Complete set trade-in",
          subtitle: "Maria S. - Star Wars Millennium Falcon ($145 credit)",
          time: "1 hour ago",
        },
        {
          id: "lego-4",
          type: "knowledge",
          title: "New sets indexed",
          subtitle: "24 new 2025 sets added to catalog",
          time: "2 hours ago",
        },
      ],
      programs: [
        {
          id: "prg_replay",
          name: "LEGO Replay",
          type: "takeback",
          status: "active",
        },
        {
          id: "prg_certified",
          name: "Certified Pre-Owned",
          type: "resale",
          status: "active",
        },
        {
          id: "prg_parts",
          name: "Parts Marketplace",
          type: "resale",
          status: "draft",
        },
      ],
    };
  } else if (tenantId === "nike") {
    return {
      stats: {
        ...DEFAULT_STATS,
        products: 15,
        knowledgeItems: 42,
        knowledgeIndexed: 40,
        knowledgeHealth: 95,
        countsByType: {
          product: 15,
          material: 8,
          policy: 6,
          faq: 10,
          training: 3,
          custom: 0,
        },
        aiConversations: 234,
        pinTakebacks: 127,
        activePrograms: 2,
        totalPrograms: 3,
        treasuryBalance: 28940,
        creditIssued: 8940,
        activeStores: 4,
      },
      activities: [
        {
          id: "nike-1",
          type: "takeback",
          title: "Trade-in completed",
          subtitle: "Marcus W. - Air Jordan 1 Retro High ($81 credit)",
          time: "12 min ago",
        },
        {
          id: "nike-2",
          type: "conversation",
          title: "AI Educator conversation",
          subtitle:
            "Guest asked about Pegasus 41 vs Vomero 18 for marathon training",
          time: "28 min ago",
        },
        {
          id: "nike-3",
          type: "takeback",
          title: "Trade-in completed",
          subtitle: "Jasmine T. - Nike Dunk Low + Air Force 1 ($82 credit)",
          time: "1 hour ago",
        },
        {
          id: "nike-4",
          type: "knowledge",
          title: "Knowledge indexed",
          subtitle: "Nike Refurbished trade-in policies synced",
          time: "2 hours ago",
        },
      ],
      programs: [
        {
          id: "prg_refurbished",
          name: "Nike Refurbished",
          type: "takeback",
          status: "active",
        },
        {
          id: "prg_jordan",
          name: "Jordan Collection Buyback",
          type: "takeback",
          status: "active",
        },
        {
          id: "prg_running",
          name: "Nike Running Trade-Up",
          type: "takeback",
          status: "draft",
        },
      ],
    };
  } else if (tenantId === "adidas") {
    return {
      stats: {
        ...DEFAULT_STATS,
        products: 18,
        knowledgeItems: 38,
        knowledgeIndexed: 36,
        knowledgeHealth: 95,
        countsByType: {
          product: 18,
          material: 6,
          policy: 5,
          faq: 6,
          training: 3,
          custom: 0,
        },
        aiConversations: 198,
        pinTakebacks: 89,
        activePrograms: 2,
        totalPrograms: 3,
        treasuryBalance: 24500,
        creditIssued: 7200,
        activeStores: 6,
      },
      activities: [
        {
          id: "adidas-1",
          type: "takeback",
          title: "Trade-in completed",
          subtitle: "James K. - Ultraboost 22 + Stan Smith ($72 credit)",
          time: "18 min ago",
        },
        {
          id: "adidas-2",
          type: "conversation",
          title: "AI Educator conversation",
          subtitle: "Guest asked about Boost vs Lightstrike cushioning",
          time: "42 min ago",
        },
        {
          id: "adidas-3",
          type: "takeback",
          title: "Trade-in completed",
          subtitle: "Sarah L. - Forum Low + Samba OG ($65 credit)",
          time: "1 hour ago",
        },
        {
          id: "adidas-4",
          type: "knowledge",
          title: "Knowledge indexed",
          subtitle: "adidas Infinite Play trade-in policies synced",
          time: "3 hours ago",
        },
      ],
      programs: [
        {
          id: "prg_infinite_play",
          name: "Infinite Play",
          type: "takeback",
          status: "active",
        },
        {
          id: "prg_originals",
          name: "Originals Buyback",
          type: "takeback",
          status: "active",
        },
        {
          id: "prg_performance",
          name: "Performance Trade-Up",
          type: "takeback",
          status: "draft",
        },
      ],
    };
  } else if (tenantId === "demobrand") {
    return {
      stats: {
        ...DEFAULT_STATS,
        products: 12,
        knowledgeItems: 37,
        knowledgeIndexed: 36,
        knowledgeHealth: 85,
        countsByType: {
          product: 12,
          material: 8,
          policy: 6,
          faq: 4,
          training: 6,
          custom: 1,
        },
        aiConversations: 47,
        pinTakebacks: 12,
        activePrograms: 1,
        totalPrograms: 1,
        treasuryBalance: 847,
        creditIssued: 420,
        activeStores: 1,
      },
      activities: [
        {
          id: "demo-1",
          type: "conversation",
          title: "AI Educator conversation",
          subtitle: "Guest asked about Velocity Pro running shoes",
          time: "5 min ago",
        },
        {
          id: "demo-2",
          type: "takeback",
          title: "Trade-in submitted",
          subtitle: "Customer - ThermoCore Jacket ($45 credit)",
          time: "20 min ago",
        },
        {
          id: "demo-3",
          type: "knowledge",
          title: "Knowledge indexed",
          subtitle: "Products synced to knowledge base",
          time: "1 hour ago",
        },
        {
          id: "demo-4",
          type: "conversation",
          title: "AI Educator conversation",
          subtitle: "Guest asked about Adventure Pro Backpack features",
          time: "2 hours ago",
        },
      ],
      programs: [
        {
          id: "prg_demobrand_cpo",
          name: "Demo Brand Certified Pre-Owned",
          type: "takeback",
          status: "active",
        },
      ],
    };
  }

  // Default: Lululemon
  return {
    stats: {
      ...DEFAULT_STATS,
      products: 8,
      knowledgeItems: 35,
      knowledgeIndexed: 33,
      knowledgeHealth: 94,
      countsByType: {
        product: 8,
        material: 6,
        policy: 5,
        faq: 13,
        training: 3,
        custom: 0,
      },
      aiConversations: 47,
      pinTakebacks: 12,
      activePrograms: 1,
      totalPrograms: 2,
      treasuryBalance: 847,
      creditIssued: 420,
      activeStores: 3,
    },
    activities: [
      {
        id: "lulu-1",
        type: "conversation",
        title: "AI Educator conversation",
        subtitle: "Guest asked about Align pants sizing",
        time: "5 min ago",
      },
      {
        id: "lulu-2",
        type: "takeback",
        title: "Trade-in submitted",
        subtitle: "Emily R. - Wunder Train leggings ($35 credit)",
        time: "20 min ago",
      },
      {
        id: "lulu-3",
        type: "knowledge",
        title: "Knowledge indexed",
        subtitle: "Products synced to knowledge base",
        time: "1 hour ago",
      },
      {
        id: "lulu-4",
        type: "conversation",
        title: "AI Educator conversation",
        subtitle: "Guest asked about Nulu vs Everlux fabric",
        time: "2 hours ago",
      },
    ],
    programs: [
      {
        id: "prg_likenew",
        name: "Like New Trade-In",
        type: "takeback",
        status: "active",
      },
      {
        id: "prg_resale",
        name: "Like New Resale",
        type: "resale",
        status: "draft",
      },
    ],
  };
}

export const Route = createFileRoute("/")({
  beforeLoad: () => {
    // Parse separately so the redirect throw is never caught
    let session = null;
    try {
      const stored = localStorage.getItem("pic_auth_session");
      if (stored) session = JSON.parse(stored);
    } catch {
      // ignore malformed JSON
    }
    if (session?.user?.tenantPlan === "free") {
      throw redirect({ to: "/market-intelligence" });
    }
  },
  component: Overview,
});

function Overview() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const { tenantId } = useTenant();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Free plan users should never see Overview — redirect to Market Intelligence
  useEffect(() => {
    if (user?.tenantPlan === "free") {
      void navigate({ to: "/market-intelligence" });
    }
  }, [user?.tenantPlan, navigate]);

  // Render nothing while redirect is pending (prevents flash)
  if (user?.tenantPlan === "free") return null;

  // Get tenant-specific branding
  const branding = TENANT_BRANDING[tenantId] || TENANT_BRANDING.lululemon;

  useEffect(() => {
    loadDashboardData();
  }, [tenantId]);

  const loadDashboardData = async () => {
    setLoading(true);
    const demoData = getTenantDemoData(tenantId);

    try {
      // Fetch all stats in parallel
      const [
        knowledgeRes,
        programsRes,
        pinStatsRes,
        treasuryRes,
        storesRes,
        takebacksRes,
      ] = await Promise.all([
        fetch(`/api/v1/knowledge/stats?tenantId=${tenantId}`).catch(() => null),
        fetch(`/api/programs?tenantId=${tenantId}`).catch(() => null),
        fetch(`/api/v1/pin/stats?tenantId=${tenantId}`).catch(() => null),
        fetch(`/api/treasury?tenantId=${tenantId}`).catch(() => null),
        fetch(`/api/v1/config/stores?tenantId=${tenantId}`).catch(() => null),
        fetch(`/api/v1/pin/takebacks?tenantId=${tenantId}&limit=5`).catch(
          () => null,
        ),
      ]);

      // Parse knowledge stats
      let knowledgeData = {
        total: 0,
        indexed: 0,
        healthScore: 0,
        countsByType: {
          product: 0,
          material: 0,
          policy: 0,
          faq: 0,
          training: 0,
          custom: 0,
        },
      };
      if (knowledgeRes?.ok) {
        const data = await knowledgeRes.json();
        knowledgeData = {
          total: data.total || 0,
          indexed: data.indexed || 0,
          healthScore: data.healthScore || 0,
          countsByType: data.countsByType || knowledgeData.countsByType,
        };
      }

      // Parse programs
      let programsList: Program[] = [];
      let activePrograms = 0;
      let totalPrograms = 0;
      if (programsRes?.ok) {
        const data = await programsRes.json();
        programsList = (data.programs || []).map(
          (p: { id: string; name: string; type: string; status: string }) => ({
            id: p.id,
            name: p.name,
            type: p.type,
            status: p.status,
          }),
        );
        totalPrograms = programsList.length;
        activePrograms = programsList.filter(
          (p) => p.status === "active",
        ).length;
      }

      // Parse PIN stats
      let aiConversations = 0;
      let aiMinutesUsed = 0;
      let pinTakebacks = 0;
      if (pinStatsRes?.ok) {
        const data = await pinStatsRes.json();
        aiConversations = data.totalSessions || data.conversations || 0;
        aiMinutesUsed = data.totalMinutes || 0;
        pinTakebacks = data.takebacks || 0;
      }

      // Parse treasury
      let treasuryBalance = 0;
      let creditIssued = 0;
      if (treasuryRes?.ok) {
        const data = await treasuryRes.json();
        treasuryBalance =
          (data.data?.balance?.current_balance_cents || 0) / 100;
        // Sum recent deposits as credit issued approximation
        creditIssued =
          (data.data?.recent_deposits || []).reduce(
            (sum: number, d: { amount_cents: number }) =>
              sum + (d.amount_cents || 0),
            0,
          ) / 100;
      }

      // Parse stores
      let activeStores = 0;
      if (storesRes?.ok) {
        const data = await storesRes.json();
        activeStores = data.count || data.stores?.length || 0;
      }

      // Parse recent takebacks for activity
      const activities: RecentActivity[] = [];
      if (takebacksRes?.ok) {
        const data = await takebacksRes.json();
        (data.takebacks || []).slice(0, 3).forEach(
          (
            tb: {
              id: string;
              customerName?: string;
              creditValue?: number;
              createdAt?: string;
            },
            idx: number,
          ) => {
            activities.push({
              id: `tb-${idx}`,
              type: "takeback",
              title: "Trade-in completed",
              subtitle: `${tb.customerName || "Customer"} - $${tb.creditValue || 0} credit`,
              time: tb.createdAt ? formatTimeAgo(tb.createdAt) : "Recently",
            });
          },
        );
      }

      // Fill with demo activities if needed
      if (activities.length < 4) {
        activities.push(...demoData.activities.slice(0, 4 - activities.length));
      }

      // Build final stats, using API data with demo fallbacks
      const finalStats: DashboardStats = {
        knowledgeItems: knowledgeData.total || demoData.stats.knowledgeItems,
        knowledgeIndexed:
          knowledgeData.indexed || demoData.stats.knowledgeIndexed,
        knowledgeHealth:
          knowledgeData.healthScore || demoData.stats.knowledgeHealth,
        countsByType:
          knowledgeData.total > 0
            ? knowledgeData.countsByType
            : demoData.stats.countsByType,
        products:
          knowledgeData.countsByType?.product || demoData.stats.products,
        activePrograms:
          totalPrograms > 0 ? activePrograms : demoData.stats.activePrograms,
        totalPrograms:
          totalPrograms > 0 ? totalPrograms : demoData.stats.totalPrograms,
        aiConversations: aiConversations || demoData.stats.aiConversations,
        aiMinutesUsed: aiMinutesUsed || demoData.stats.aiMinutesUsed,
        pinTakebacks: pinTakebacks || demoData.stats.pinTakebacks,
        treasuryBalance: treasuryBalance || demoData.stats.treasuryBalance,
        creditIssued: creditIssued || demoData.stats.creditIssued,
        activeStores: activeStores || demoData.stats.activeStores,
      };

      setStats(finalStats);
      setPrograms(programsList.length > 0 ? programsList : demoData.programs);
      setRecentActivity(activities.slice(0, 4));
    } catch (error) {
      console.debug("Failed to load dashboard data:", error);
      setStats(demoData.stats);
      setPrograms(demoData.programs);
      setRecentActivity(demoData.activities);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "conversation":
        return (
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: "rgba(0,251,169,0.15)" }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#00FBA9"
              strokeWidth="2"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
        );
      case "takeback":
        return (
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: "rgba(57,65,235,0.15)" }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#3941EB"
              strokeWidth="2"
            >
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
        );
      case "knowledge":
        return (
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: "rgba(255,184,0,0.15)" }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#FFB800"
              strokeWidth="2"
            >
              <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  // Calculate pending knowledge items for quick actions
  const pendingKnowledge =
    (stats?.knowledgeItems || 0) - (stats?.knowledgeIndexed || 0);
  const draftPrograms = programs.filter((p) => p.status === "draft");

  return (
    <div className="max-w-6xl space-y-8">
      {/* Hero Section */}
      <div
        className="rounded-2xl p-8 relative overflow-hidden"
        style={{ backgroundColor: "#171717" }}
      >
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-12 h-12 flex items-center justify-center"
              style={{ backgroundColor: branding.primaryColor }}
            >
              <span className="text-white font-bold text-lg">
                {branding.initial}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-light text-white">
                {branding.name}
              </h1>
              <p className="text-sm" style={{ color: "rgba(255,255,255,0.6)" }}>
                {branding.tagline}
              </p>
            </div>
          </div>
          <p className="text-white/80 max-w-xl">{branding.description}</p>
        </div>
        <div
          className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl opacity-20"
          style={{ backgroundColor: branding.primaryColor }}
        />
      </div>

      {/* Key Metrics - 6 columns */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard
          label="Products"
          value={stats?.products || 0}
          subtext="In catalog"
          icon={
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          }
        />
        <MetricCard
          label="Knowledge"
          value={stats?.knowledgeItems || 0}
          subtext={`${stats?.knowledgeIndexed || 0} indexed`}
          color="#00FBA9"
          icon={
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          }
        />
        <MetricCard
          label="Programs"
          value={stats?.activePrograms || 0}
          subtext={`${stats?.totalPrograms || 0} total`}
          color="#8B5CF6"
          icon={
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          }
        />
        <MetricCard
          label="AI Chats"
          value={stats?.aiConversations || 0}
          subtext={`${stats?.aiMinutesUsed?.toFixed(0) || 0} min`}
          color="#3941EB"
          icon={
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          }
        />
        <MetricCard
          label="Takebacks"
          value={stats?.pinTakebacks || 0}
          subtext={`$${(stats?.creditIssued || 0).toLocaleString()} issued`}
          color="#C41230"
          icon={
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          }
        />
        <MetricCard
          label="Treasury"
          value={`$${((stats?.treasuryBalance || 0) / 1000).toFixed(1)}k`}
          subtext={`${stats?.activeStores || 0} stores`}
          color="#059669"
          icon={
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
      </div>

      {/* Two Column: Knowledge Health + Program Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Knowledge Health */}
        <div
          className="bg-white border p-6"
          style={{ borderColor: "rgba(169,169,169,0.2)" }}
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-medium" style={{ color: "#171717" }}>
                Knowledge Base Health
              </h2>
              <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
                AI Educator knowledge coverage
              </p>
            </div>
            <Link
              to="/knowledge"
              className="text-xs px-3 py-1.5 rounded-full"
              style={{
                backgroundColor: "rgba(0,251,169,0.1)",
                color: "#00A672",
              }}
            >
              Manage
            </Link>
          </div>

          <div className="grid grid-cols-5 gap-3">
            <KnowledgeTypeCard
              type="Products"
              count={stats?.countsByType?.product || 0}
            />
            <KnowledgeTypeCard
              type="Materials"
              count={stats?.countsByType?.material || 0}
            />
            <KnowledgeTypeCard
              type="Policies"
              count={stats?.countsByType?.policy || 0}
            />
            <KnowledgeTypeCard
              type="FAQs"
              count={stats?.countsByType?.faq || 0}
            />
            <KnowledgeTypeCard
              type="Training"
              count={stats?.countsByType?.training || 0}
            />
          </div>
        </div>

        {/* Program Status */}
        <div
          className="bg-white border p-6"
          style={{ borderColor: "rgba(169,169,169,0.2)" }}
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-medium" style={{ color: "#171717" }}>
                Program Status
              </h2>
              <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
                {stats?.activePrograms || 0} active of{" "}
                {stats?.totalPrograms || 0}
              </p>
            </div>
            <Link
              to="/programs"
              className="text-xs px-3 py-1.5 rounded-full"
              style={{
                backgroundColor: "rgba(139,92,246,0.1)",
                color: "#7C3AED",
              }}
            >
              View All
            </Link>
          </div>

          <div className="space-y-3">
            {programs.slice(0, 4).map((program) => (
              <Link
                key={program.id}
                to={`/programs/${program.id}` as "/programs"}
                className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor:
                        program.status === "active"
                          ? "#00FBA9"
                          : program.status === "draft"
                            ? "#A9A9A9"
                            : "#FFB800",
                    }}
                  />
                  <span
                    className="text-sm font-medium"
                    style={{ color: "#171717" }}
                  >
                    {program.name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor:
                        program.status === "active"
                          ? "rgba(0,251,169,0.1)"
                          : "rgba(169,169,169,0.1)",
                      color:
                        program.status === "active" ? "#00A672" : "#A9A9A9",
                    }}
                  >
                    {program.status}
                  </span>
                  <span className="text-xs" style={{ color: "#A9A9A9" }}>
                    {program.type}
                  </span>
                </div>
              </Link>
            ))}
            {programs.length === 0 && (
              <div className="text-center py-6">
                <p className="text-sm" style={{ color: "#A9A9A9" }}>
                  No programs configured
                </p>
                <Link
                  to="/programs"
                  className="text-xs mt-2 inline-block"
                  style={{ color: "#7C3AED" }}
                >
                  Create a program
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Two Column: Recent Activity + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div
          className="bg-white border p-6"
          style={{ borderColor: "rgba(169,169,169,0.2)" }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-medium" style={{ color: "#171717" }}>
              Recent Activity
            </h2>
            <Link to="/pin" className="text-xs" style={{ color: "#A9A9A9" }}>
              View all
            </Link>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex gap-3 animate-pulse">
                  <div className="w-8 h-8 rounded-full bg-gray-100" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-100 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : recentActivity.length > 0 ? (
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3">
                  {getActivityIcon(activity.type)}
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm font-medium truncate"
                      style={{ color: "#171717" }}
                    >
                      {activity.title}
                    </p>
                    <p
                      className="text-xs truncate"
                      style={{ color: "#A9A9A9" }}
                    >
                      {activity.subtitle}
                    </p>
                  </div>
                  <span
                    className="text-xs flex-shrink-0"
                    style={{ color: "#A9A9A9" }}
                  >
                    {activity.time}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p
              className="text-sm text-center py-8"
              style={{ color: "#A9A9A9" }}
            >
              No recent activity
            </p>
          )}
        </div>

        {/* Quick Actions - Dynamic */}
        <div
          className="bg-white border p-6"
          style={{ borderColor: "rgba(169,169,169,0.2)" }}
        >
          <h2 className="font-medium mb-6" style={{ color: "#171717" }}>
            Quick Actions
          </h2>

          <div className="space-y-3">
            {/* Dynamic action: Pending knowledge */}
            {pendingKnowledge > 0 && (
              <QuickActionRow
                icon={
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#FFB800"
                    strokeWidth="1.5"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 8v4m0 4h.01" />
                  </svg>
                }
                title={`${pendingKnowledge} pending knowledge items`}
                description="Items waiting to be indexed"
                href="/knowledge"
                highlight
              />
            )}

            {/* Dynamic action: Draft programs */}
            {draftPrograms.length > 0 && (
              <QuickActionRow
                icon={
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8B5CF6"
                    strokeWidth="1.5"
                  >
                    <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                }
                title={`Configure ${draftPrograms[0].name}`}
                description="Complete program setup"
                href={`/programs/${draftPrograms[0].id}`}
              />
            )}

            <QuickActionRow
              icon={
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              }
              title="Manage Knowledge"
              description="Add materials, policies, and training content"
              href="/knowledge"
            />
            <QuickActionRow
              icon={
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              }
              title="Physical Intelligence"
              description="View kiosk activity and AI conversations"
              href="/pin"
            />
            <QuickActionRow
              icon={
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              }
              title="View Programs"
              description="Manage trade-in and resale programs"
              href="/programs"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  subtext,
  color,
  icon,
}: {
  label: string;
  value: number | string;
  subtext: string;
  color?: string;
  icon: React.ReactNode;
}) {
  return (
    <div
      className="bg-white border p-4"
      style={{ borderColor: "rgba(169,169,169,0.2)" }}
    >
      <div className="flex items-start justify-between mb-2">
        <p
          className="text-xs font-medium uppercase tracking-wider"
          style={{ color: "#A9A9A9" }}
        >
          {label}
        </p>
        <span style={{ color: color || "#A9A9A9" }}>{icon}</span>
      </div>
      <p className="text-2xl font-light" style={{ color: color || "#171717" }}>
        {value}
      </p>
      <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
        {subtext}
      </p>
    </div>
  );
}

function QuickActionRow({
  icon,
  title,
  description,
  href,
  highlight,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  href: string;
  highlight?: boolean;
}) {
  return (
    <Link
      to={href as "/"}
      className="flex items-center gap-4 p-3 rounded-lg transition-colors hover:bg-gray-50"
      style={{
        backgroundColor: highlight ? "rgba(255,184,0,0.05)" : undefined,
      }}
    >
      <div
        className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg"
        style={{ backgroundColor: "#FAFAFA", color: "#A9A9A9" }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="text-sm font-medium"
          style={{ color: highlight ? "#B45309" : "#171717" }}
        >
          {title}
        </p>
        <p className="text-xs truncate" style={{ color: "#A9A9A9" }}>
          {description}
        </p>
      </div>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#A9A9A9"
        strokeWidth="1.5"
      >
        <path d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  );
}

function KnowledgeTypeCard({ type, count }: { type: string; count: number }) {
  return (
    <div
      className="text-center p-3 rounded-lg"
      style={{ backgroundColor: "#FAFAFA" }}
    >
      <p
        className="text-xs font-medium uppercase tracking-wider mb-1"
        style={{ color: "#A9A9A9" }}
      >
        {type}
      </p>
      <p
        className="text-xl font-light"
        style={{ color: count > 0 ? "#171717" : "#A9A9A9" }}
      >
        {count}
      </p>
    </div>
  );
}
